import type { TContextFeature } from '@safesystem/geoserver-api';
import { FetchStatus, type ISystemLayers } from '#lib/types.js';
import { contextLayerId, geoServer } from '#lib/config.js';
import type { SummaryData, ConfigData } from '#lib/apps/types.js';
import { appStore } from '#lib/stores/appStore.svelte.js';
import { orderLayers } from '#lib/components/map/layers.js';
import { apiKeyStore } from '#lib/stores/apiKeyStore.svelte.js';
import { getLogger } from '@logtape/logtape';

const log = getLogger(['platform', 'geodata-store']);

// Shares one in-flight load between overlapping callers; the `*Ready` guards only cover a completed one.
function dedupe(load: (apiKey: string) => Promise<void>) {
	let inFlight: Promise<void> | null = null;
	const run = (apiKey: string) => (inFlight ??= load(apiKey).finally(() => (inFlight = null)));
	run.reset = () => (inFlight = null);
	return run;
}

function createGeodataStore() {
	// Shape-agnostic: each app's loaders fill these and consumers narrow at the call site.
	let summaryData = $state<SummaryData>({});
	let summaryDataReady = $state(false);
	let fetchStatus = $state<FetchStatus>(FetchStatus.IDLE);
	let systemData = $state<unknown>({});
	let systemDataReady = $state(false);
	let configData = $state<ConfigData>({});
	let configDataReady = $state(false);
	let contextLayer = $state<TContextFeature | null>(null);

	// Summary and system load concurrently into one status: EMPTY (no data for the app) outranks
	// FAILURE, which outranks SUCCESS.
	const succeed = () => {
		if (fetchStatus === FetchStatus.IDLE || fetchStatus === FetchStatus.LOADING)
			fetchStatus = FetchStatus.SUCCESS;
	};
	const fail = () => {
		if (fetchStatus !== FetchStatus.EMPTY) fetchStatus = FetchStatus.FAILURE;
	};

	const loadSystem = dedupe(async (apiKey) => {
		const config = appStore.app;
		if (!config) {
			log.error('loadSystemData: no active AppConfig — call appStore.setApp() first');
			return;
		}
		try {
			const data = await config.loaders.system(apiKey);
			if (data === null) {
				fetchStatus = FetchStatus.EMPTY;
				log.info('loadSystemData: no {configId} data for this key', { configId: config.id });
				return;
			}
			systemData = data;
			systemDataReady = true;
			log.info('loadSystemData: {configId} system data loaded', { configId: config.id });
		} catch (err) {
			fail();
			log.error('loadSystemData error: {error}', { error: err });
		}
	});

	const loadConfig = dedupe(async (apiKey) => {
		const cred = apiKeyStore.credentials;
		if (!cred) {
			log.error('loadConfigData: no credentials — validate the api key first');
			return;
		}
		try {
			const config = await geoServer.getConfiguration(apiKey, cred.namespace);
			if (!config) throw new Error('Configuration data unavailable');
			configData = config;
			configDataReady = true;
			log.info('Application configuration data loaded.');
		} catch (err) {
			log.error('loadConfigData error: {error}', { error: err });
		}
	});

	// The returned tile template already embeds the key, so MapLibre needs no auth headers.
	const loadContext = dedupe(async (apiKey) => {
		try {
			const layer = await geoServer.getContextLayer(apiKey, contextLayerId);
			if (layer) {
				contextLayer = layer;
				log.info('loadContextLayer: retrieved {layerId}', { layerId: layer.id });
			} else {
				log.error('loadContextLayer: context layer not found');
			}
		} catch (err) {
			log.error('loadContextLayer error: {error}', { error: err });
		}
	});

	return {
		get summaryData() {
			return summaryData;
		},
		get contextLayer() {
			return contextLayer;
		},
		get summaryDataReady() {
			return summaryDataReady;
		},
		get fetchStatus() {
			return fetchStatus;
		},
		get systemData() {
			return systemData;
		},
		get systemDataReady() {
			return systemDataReady;
		},
		get configData() {
			return configData;
		},

		async loadSummaryData(apiKey: string) {
			if (fetchStatus === FetchStatus.LOADING) return;
			const config = appStore.app;
			if (!config) {
				log.error('loadSummaryData: no active AppConfig — call appStore.setApp() first');
				return;
			}
			if (!config.loaders.summary) {
				// No summary loader: succeed empty so the loading gate clears.
				summaryData = {};
				summaryDataReady = true;
				succeed();
				return;
			}
			if (fetchStatus === FetchStatus.IDLE) fetchStatus = FetchStatus.LOADING;
			try {
				summaryData = await config.loaders.summary(apiKey);
				summaryDataReady = true;
				succeed();
				log.info('loadSummaryData: {configId} summary loaded', { configId: config.id });
			} catch (err) {
				fail();
				log.error('loadSummaryData error: {error}', { error: err });
			}
		},

		async loadSystemData(apiKey: string) {
			if (systemDataReady) return;
			return loadSystem(apiKey);
		},

		async loadConfigData(apiKey: string) {
			if (configDataReady) return;
			return loadConfig(apiKey);
		},

		async loadContextLayer(apiKey: string) {
			if (contextLayer) return;
			// The 'full' basemap already includes roads.
			if (appStore.app?.basemap === 'full') return;
			return loadContext(apiKey);
		},

		/** GeoServer abstract of a loaded system layer, shown by its info buttons. */
		layerAbstract(systemId: string) {
			return orderLayers(systemData as ISystemLayers).find((l) => l.id === systemId)?.abstract;
		},

		// configData and contextLayer are per api key, not per app, so they survive.
		resetAppData() {
			summaryData = {};
			summaryDataReady = false;
			fetchStatus = FetchStatus.IDLE;
			systemData = {};
			systemDataReady = false;
			loadSystem.reset();
		}
	};
}

export const geodataStore = createGeodataStore();
